import type { MccSuggestion, MerchantCategoryCodeSummary, NormalizedMcc } from '@ballerine/common';

/**
 * Maps a single digital-footprint `MccSuggestion` to the normalized MCC used by
 * {@link OperationalActivitySection} (same display shape as point-of-sale operational proof).
 */
const mapMccSuggestionToNormalizedMcc = (
  suggestion: MccSuggestion | null | undefined,
): NormalizedMcc | null => {
  const code = suggestion?.mcc?.trim();

  if (!code) {
    return null;
  }

  return {
    code,
    category: suggestion?.description ?? null,
    reasoning: suggestion?.reasoning ?? null,
  };
};

/**
 * Maps digital-footprint / report `mcc` summary shape to the normalized MCC used by
 * {@link OperationalActivitySection} (same display shape as point-of-sale operational proof).
 */
export const mapMerchantCategorySummaryToNormalizedMcc = (
  summary: MerchantCategoryCodeSummary | null | undefined,
): NormalizedMcc | null => mapMccSuggestionToNormalizedMcc(summary?.primary);

/**
 * Maps the digital-footprint `mcc.alternatives` array to normalized MCCs, dropping any
 * entry without a code so the section only renders real alternatives.
 */
export const mapMerchantCategoryAlternativesToNormalizedMcc = (
  summary: MerchantCategoryCodeSummary | null | undefined,
): NormalizedMcc[] =>
  (summary?.alternatives ?? [])
    .map(mapMccSuggestionToNormalizedMcc)
    .filter((mcc): mcc is NormalizedMcc => Boolean(mcc));
